import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { shiftWeeks, type WeekItem, type Coverage } from '@familyhub/shared';
import { useSession } from '../session';
import { adb } from './db';
import { useAgendaWeek } from './useAgenda';
import { parentColor, CAREGIVER_COLOR, orderByParticipantName, spinePaint } from './labels';
import { formatDayHeading, formatStripDay, formatSpanTime, formatWeekRange, todayKey } from './datetime';
import { ActivitySheet, ChildcareSheet, type Member, type AddKind } from './sheets';
import { CoverageBar } from './CoverageBar';
import { GearIcon } from '../components/GearIcon';
import { Loading } from '../components/Loading';

/**
 * The week — the interface (docs/AGENDA.md §1). Each day follows one hierarchy:
 * **date → where is the child (childcare) → what are we doing (activities) →
 * does it line up (coverage)**. Activities are one combined chronological list,
 * not per-parent columns; each carries its person's colour and, underneath, the
 * answer to the only question that matters — is the child covered for it.
 *
 * Everything reads from the local mirror, so it works with no signal.
 */

type Editor =
  | null
  | { kind: 'activity'; day: string; rowId?: string }
  | { kind: 'childcare'; day: string; rowId?: string };

/** The week-strip dot: calm (something's on, nothing wrong), a warning, or nothing to show at all. */
type DayStatus = 'empty' | 'calm' | 'warn';

function dayStatus(
  bucket: { activities: WeekItem[]; childcare: WeekItem[] },
  coverage: Coverage,
): DayStatus {
  if (bucket.activities.length === 0 && bucket.childcare.length === 0) return 'empty';
  const warn = bucket.activities.some((a) => {
    const cov = coverage.activityCoverage[a.key];
    return (cov && cov.state !== 'covered') || coverage.conflictKeys.includes(a.key) || coverage.carConflictKeys.includes(a.key);
  });
  return warn ? 'warn' : 'calm';
}

export function WeekScreen() {
  const session = useSession();
  const userId = session.status === 'in' ? session.userId : '';
  const me = session.status === 'in' ? session.me : null;

  const members: Member[] = useMemo(() => {
    if (me?.members?.length) return me.members;
    return userId ? [{ id: userId, display_name: 'Jij' }] : [];
  }, [me, userId]);
  const memberIds = useMemo(() => members.map((m) => m.id), [members]);

  const [refIso, setRefIso] = useState(() => new Date().toISOString());
  const [editor, setEditor] = useState<Editor>(null);
  const dayRefs = useRef(new Map<string, HTMLElement>());

  const week = useAgendaWeek(refIso);
  const pending = useLiveQuery(() => adb.outbox.count(), [], 0);

  const nameOf = useMemo(() => {
    const users = new Map(members.map((m) => [m.id, m.display_name]));
    const persons = new Map((week?.people ?? []).map((p) => [p.id, p.name]));
    return (kind: string, id: string) => (kind === 'user' ? users.get(id) : persons.get(id)) ?? '?';
  }, [members, week?.people]);

  const colorOf = useMemo(() => {
    const persons = new Map((week?.people ?? []).map((p) => [p.id, p.color]));
    return (kind: string, id: string) => (kind === 'user' ? parentColor(id, memberIds) : persons.get(id) || CAREGIVER_COLOR);
  }, [memberIds, week?.people]);

  const labelFor = (key: string) => {
    const [kind, id] = [key.slice(0, key.indexOf(':')), key.slice(key.indexOf(':') + 1)];
    return { name: nameOf(kind, id), color: colorOf(kind, id) };
  };

  if (!week) {
    return (
      <div className="app">
        <Topbar />
        <Loading />
      </div>
    );
  }

  const { days, items, coverage } = week;
  const today = todayKey();
  const inThisWeek = days.includes(today);
  const defaultDay = inThisWeek ? today : days[0]!;

  const byDay = new Map<string, { activities: WeekItem[]; childcare: WeekItem[] }>();
  for (const d of days) byDay.set(d, { activities: [], childcare: [] });
  for (const item of items) {
    const bucket = byDay.get(item.day);
    if (!bucket) continue;
    if (item.type === 'activity') bucket.activities.push(item);
    else if (item.type === 'childcare') bucket.childcare.push(item);
  }
  const byStart = (a: WeekItem, b: WeekItem) =>
    a.all_day === b.all_day ? a.starts_at.localeCompare(b.starts_at) : a.all_day ? -1 : 1;

  const statusOf = (day: string) => dayStatus(byDay.get(day) ?? { activities: [], childcare: [] }, coverage);
  const scrollToDay = (day: string) => dayRefs.current.get(day)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const open = (kind: 'activity' | 'childcare', rowId?: string, day: string = defaultDay) =>
    setEditor({ kind, rowId, day });

  const common = {
    members,
    people: week.people,
    items,
    userId,
    defaultDay: editor?.day ?? defaultDay,
    onClose: () => setEditor(null),
    // Only while creating: switching type keeps the day you were adding to.
    onSwitchKind: (kind: AddKind) => setEditor({ kind, day: editor?.day ?? defaultDay }),
  };
  const editRec = editor?.rowId;

  return (
    <div className="app">
      <Topbar />

      {session.status === 'in' && pending > 0 && (
        <div className="banner">
          {pending} {pending === 1 ? 'wijziging wacht' : 'wijzigingen wachten'} op synchronisatie
        </div>
      )}

      <div className="agenda-nav">
        <button className="btn btn-quiet" aria-label="Vorige week" onClick={() => setRefIso(shiftWeeks(refIso, -1))}>‹</button>
        <button className="agenda-week-label" onClick={() => setRefIso(new Date().toISOString())}>
          <strong>{inThisWeek ? 'Deze week' : formatWeekRange(days)}</strong>
          <span>{formatWeekRange(days)}</span>
        </button>
        <button className="btn btn-quiet" aria-label="Volgende week" onClick={() => setRefIso(shiftWeeks(refIso, 1))}>›</button>
      </div>

      <main>
        <WeekStrip days={days} today={today} statusOf={statusOf} onPick={scrollToDay} />

        {/* A first, or simply quiet, week gets one calm explanation instead of
            "Niets gepland" seven times with no idea what it's for — but the day
            sections stay, with their own +, so nothing about adding changes. */}
        {items.length === 0 && (
          <div className="agenda-orient">
            <p className="agenda-orient-lead">Deze week is nog leeg.</p>
            <p className="agenda-orient-body">
              De agenda houdt twee dingen bij: wat Jurren en Olga doen, en wie er op de kleine past. De + rechtsonder
              zet een activiteit neer; de + bij een dag hierboven is voor wie er oppast. De app laat vanzelf zien of
              het dekt.
            </p>
          </div>
        )}

        {days.map((day) => {
            const bucket = byDay.get(day) ?? { activities: [], childcare: [] };
            const activities = [...bucket.activities].sort(byStart);
            const childcare = [...bucket.childcare].sort(byStart);
            const dayItems = [...activities, ...childcare];
            return (
              <section
                className="group agenda-day"
                key={day}
                ref={(el) => {
                  if (el) dayRefs.current.set(day, el);
                  else dayRefs.current.delete(day);
                }}
              >
                <h2 className={`group-head${day === today ? ' agenda-today' : ''}`}>
                  <span>{formatDayHeading(day)}</span>
                  <button
                    className="agenda-day-add"
                    aria-label={`Toevoegen op ${formatDayHeading(day)}`}
                    onClick={() => setEditor({ kind: 'activity', day })}
                  >
                    +
                  </button>
                </h2>

                {/* Where is the child — quiet chips, deliberately not a bordered
                    card, so a caregiver window never reads as another event.
                    Quiet is the operative word: these were filled, border-less
                    tints, which made the band the loudest thing on the screen —
                    louder than the activities, which are what the household
                    actually adds to and which are drawn in plain white. The
                    weight was the wrong way round against how often each is
                    used, so the fill came down and the activities gained the
                    identity spine instead. */}
                {childcare.length > 0 && (
                  <div className="agenda-care">
                    {childcare.map((c) => {
                      const cgs = (c.caregiverKeys ?? []).map(labelFor);
                      return (
                        <button
                          className="agenda-care-chip"
                          key={c.key}
                          style={{ '--care-color': cgs[0]?.color ?? CAREGIVER_COLOR } as React.CSSProperties}
                          onClick={() => open('childcare', c.rowId)}
                        >
                          👶 bij {cgs.map((x) => x.name).join(' & ') || '?'}
                          <span className="agenda-care-time">{formatSpanTime(c)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Where the day's childcare has a hole, once, positioned in
                    time — see CoverageBar. Nothing at all on a calm day. */}
                <CoverageBar
                  gaps={coverage.uncoveredByDay[day] ?? []}
                  covered={coverage.coveredByDay[day] ?? []}
                  labelFor={labelFor}
                />

                {/* What are we doing, and — on the activity itself — the clashes
                    that belong to *it* rather than to the day. */}
                {activities.length === 0 && childcare.length === 0 ? (
                  <p className="agenda-empty">Niets gepland</p>
                ) : activities.length > 0 ? (
                  <div className="card">
                    {activities.map((a) => (
                      <ActivityRow
                        key={a.key}
                        item={a}
                        dayItems={dayItems}
                        conflictKeys={coverage.conflictKeys}
                        carConflictKeys={coverage.carConflictKeys}
                        labelFor={labelFor}
                        onOpen={() => open('activity', a.rowId)}
                      />
                    ))}
                  </div>
                ) : null}
              </section>
            );
          })}
      </main>

      {/* Activity is the common case (docs/AGENDA.md §3: two timelines, one asked
          for far more than the other), so the FAB opens it directly; the per-day
          + still offers the choice, which is where "add opvang" lives now. */}
      <button className="fab" onClick={() => open('activity')}>
        + Activiteit
      </button>

      {editor?.kind === 'activity' && <ActivitySheet {...common} record={editRec ? week.byId.activity.get(editRec) : undefined} />}
      {editor?.kind === 'childcare' && <ChildcareSheet {...common} record={editRec ? week.byId.childcare.get(editRec) : undefined} />}
    </div>
  );
}

/**
 * The week at a glance (docs/AGENDA.md §1 promise, made literal): seven cells,
 * sticky under the topbar, so the whole week stays graspable and reachable
 * while a single day's detail fills the screen below. A dot is quiet when the
 * day is calm, a dot in the warning colour when it needs a look, and nothing at
 * all on an empty day — the same "only a real problem earns a mark" rule the
 * day rows themselves follow.
 */
function WeekStrip({
  days,
  today,
  statusOf,
  onPick,
}: {
  days: string[];
  today: string;
  statusOf: (day: string) => DayStatus;
  onPick: (day: string) => void;
}) {
  return (
    <div className="agenda-strip" role="group" aria-label="Dagen deze week">
      {days.map((day) => {
        const { weekday, num } = formatStripDay(day);
        const status = statusOf(day);
        return (
          <button
            key={day}
            type="button"
            className={`agenda-strip-day${day === today ? ' agenda-strip-today' : ''}`}
            aria-label={`Naar ${formatDayHeading(day)}${status === 'warn' ? ', let op' : ''}`}
            onClick={() => onPick(day)}
          >
            <span className="agenda-strip-wd">{weekday}</span>
            <span className="agenda-strip-num">{num}</span>
            <span className={`agenda-strip-dot agenda-strip-dot-${status}`} aria-hidden />
          </button>
        );
      })}
    </div>
  );
}

function Topbar() {
  return (
    <div className="topbar">
      <Link className="btn btn-quiet back" to="/" aria-label="Naar het menu">‹ Hub</Link>
      <h1>Agenda</h1>
      <div className="topbar-actions">
        <Link className="btn btn-quiet" to="/agenda/wijzigingen">Wijzigingen</Link>
        <Link className="btn btn-quiet" to="/agenda/mensen" aria-label="Oppas"><GearIcon /></Link>
      </div>
    </div>
  );
}

/** The other item a clash is with — the same "who overlaps" test the two sheets'
 * own draft warnings already use, just read back off the computed key sets
 * instead of re-run live (there is nothing left to compute: the coverage engine
 * already decided which keys clash, this only picks which same-day item they
 * clash *with*).
 *
 * `sharedPerson` is the part that is not symmetric between the two clash kinds,
 * and getting it wrong is silent. A *person* clash is by definition one person
 * needed twice, so the partner must share them. A *car* clash is a household
 * resource needed twice and `computeCoverage` builds it with no `sharePerson`
 * test at all — so the partner is normally the *other* parent. Requiring a
 * shared person for both (as this did) meant the named form "auto ook nodig: X"
 * could not fire in the ordinary two-parent case, and every car warning fell
 * back to the bare mark.
 */
/**
 * A title short enough to sit inside a warning line.
 *
 * The car warning names the other claim on the car, and that name is somebody's
 * own event title — "Tandarts (controle en een vulling die waarschijnlijk langer
 * duurt dan gepland)". The coverage line deliberately opts out of the row's
 * one-line clip (a truncated *sentence* loses the window it exists to give), so
 * an uncapped title wrapped that warning to four lines and made the row louder
 * than the problem. Cut at a word boundary: enough to recognise which event it
 * is, which is all the warning has to do — the full title is one tap away.
 */
function shortTitle(title: string, max = 28): string {
  if (title.length <= max) return title;
  const cut = title.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

function findPartner(
  item: WeekItem,
  dayItems: WeekItem[],
  clashKeys: string[],
  sharedPerson: boolean,
): WeekItem | undefined {
  return dayItems.find(
    (other) =>
      other.key !== item.key &&
      clashKeys.includes(other.key) &&
      (!sharedPerson || other.personKeys.some((k) => item.personKeys.includes(k))),
  );
}

function ActivityRow({
  item,
  dayItems,
  conflictKeys,
  carConflictKeys,
  labelFor,
  onOpen,
}: {
  item: WeekItem;
  dayItems: WeekItem[];
  conflictKeys: string[];
  carConflictKeys: string[];
  labelFor: (key: string) => { name: string; color: string };
  onOpen: () => void;
}) {
  const who = orderByParticipantName(item.personKeys.map(labelFor), (w) => w.name);

  // No coverage line here any more. A gap is a fact about the *day*, not about
  // each activity that happens to overlap it, and repeating it per row was this
  // screen's single largest source of noise: two activities over one uncovered
  // morning printed the identical red sentence twice, three printed it three
  // times, and it grew with exactly how busy the day was. `CoverageBar` states
  // it once, above, and positions it *in* the day — which the repetition never
  // did. What stays on the row is what genuinely belongs to the row: the
  // clashes this activity is itself party to.

  // Double-booked person and double-booked car are the same *class* of problem
  // — a resource needed in two places — so both wear the amber warn colour, one
  // tier under the coverage gap above, and both name what they clash with
  // rather than leaving it to be inferred from the rest of the day. Falling
  // back to the bare mark if no single partner is found keeps the warning
  // itself from silently disappearing in an unusual three-way clash.
  const inConflict = conflictKeys.includes(item.key);
  const clashLabel = inConflict
    ? (() => {
        const p = findPartner(item, dayItems, conflictKeys, true);
        return p ? (p.type === 'activity' ? shortTitle(p.title) : 'de opvang') : null;
      })()
    : null;

  const inCarConflict = carConflictKeys.includes(item.key);
  const carPartner = inCarConflict ? findPartner(item, dayItems, carConflictKeys, false) : undefined;
  const carClashTitle = carPartner ? shortTitle(carPartner.title) : undefined;

  return (
    <button
      className="row row-top agenda-item"
      onClick={onOpen}
      // Whose activity this is, as a spine down the row's left edge — the fact a
      // glance wants first, and until now readable only from the dim meta line
      // under the title.
      style={{ '--spine': spinePaint(who.map((w) => w.color)) } as React.CSSProperties}
    >
      <span className="agenda-time">{formatSpanTime(item)}</span>
      <span className="row-main">
        <span className="row-name agenda-title">
          {item.title}
          {item.bringsChild && <span aria-label="de kleine gaat mee"> 👶</span>}
          {item.needsCar && <span aria-label="heeft de auto nodig"> 🚗</span>}
        </span>
        <span className="row-sub agenda-who">
          {who.map((w, i) => (
            <span className="agenda-person" key={i}>
              <span className="agenda-person-dot" style={{ background: w.color }} aria-hidden />
              {w.name}
            </span>
          ))}
          {inConflict && <em className="agenda-mark warn">botst{clashLabel ? ` met ${clashLabel}` : ''}</em>}
        </span>
        {inCarConflict && (
          <span className="row-sub agenda-coverline warn">
            ⚠️ auto {carClashTitle ? `ook nodig: ${carClashTitle}` : 'is dubbel geboekt'}
          </span>
        )}
      </span>
    </button>
  );
}
